import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { usePublicSettings } from "@/hooks/useSettings";
import { usePlugins } from "@/plugins/usePlugins";
import { Compass, Sparkles, Crown, Coins, Rocket, Zap, Star, Gift, Bell, FlaskConical, ArrowRight } from "lucide-react";

export const PROMO_ICONS = {
  compass: Compass, sparkles: Sparkles, crown: Crown, coins: Coins, rocket: Rocket,
  zap: Zap, star: Star, gift: Gift, bell: Bell, flask: FlaskConical,
};

const ACCENT = {
  indigo: { icon: "text-indigo-600", chip: "bg-indigo-500/10 text-indigo-600", cta: "text-indigo-600", hover: "hover:border-indigo-400" },
  violet: { icon: "text-violet-600", chip: "bg-violet-500/10 text-violet-600", cta: "text-violet-600", hover: "hover:border-violet-400" },
  primary: { icon: "text-primary", chip: "bg-primary/10 text-primary", cta: "text-primary", hover: "hover:border-primary" },
  amber: { icon: "text-amber-600", chip: "bg-amber-500/10 text-amber-600", cta: "text-amber-600", hover: "hover:border-amber-400" },
  emerald: { icon: "text-emerald-600", chip: "bg-emerald-500/10 text-emerald-600", cta: "text-emerald-600", hover: "hover:border-emerald-400" },
  rose: { icon: "text-rose-600", chip: "bg-rose-500/10 text-rose-600", cta: "text-rose-600", hover: "hover:border-rose-400" },
};

function PromoCard({ p, prefix = "" }) {
  const Icon = PROMO_ICONS[p.icon] || Sparkles;
  const a = ACCENT[p.theme] || ACCENT.primary;
  const inner = (
    <>
      <div className="flex items-center gap-3 mb-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${a.chip}`}>
          <Icon className={`h-5 w-5 ${a.icon}`} />
        </div>
        {p.badge ? <span className={`text-[9px] uppercase tracking-[0.25em] font-bold ${a.cta}`}>{p.badge}</span> : null}
      </div>
      <h3 className="font-display text-xl font-extrabold tracking-tight text-foreground">{p.title}</h3>
      {p.tagline ? <p className="text-muted-foreground text-xs font-semibold mt-0.5">{p.tagline}</p> : null}
      {p.body ? <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{p.body}</p> : null}
      {p.cta ? (
        <span className={`inline-flex items-center gap-1.5 text-sm font-semibold mt-3 ${a.cta}`}>
          {p.cta} <ArrowRight className="h-4 w-4" />
        </span>
      ) : null}
    </>
  );
  const cls = `block rounded-2xl p-5 border border-border bg-card transition-colors ${a.hover}`;
  return p.link ? (
    <Link to={p.link} className={`${cls} group`} data-testid={`${prefix}promo-card-${p.id}`}>{inner}</Link>
  ) : (
    <div className={cls} data-testid={`${prefix}promo-card-${p.id}`}>{inner}</div>
  );
}

function Cards({ promos, prefix = "" }) {
  return promos.map((p, i) => (
    <motion.div key={p.id || i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.06 }}>
      <PromoCard p={p} prefix={prefix} />
    </motion.div>
  ));
}

export function PromoRail({ variant = "rail" }) {
  const { data: settings } = usePublicSettings();
  const { isPluginEnabled } = usePlugins();
  // A promo whose id matches an app module is an entry point into that module —
  // hide it when the module is toggled OFF so the switchboard fully hides it.
  const promos = (settings?.promos || []).filter((p) => isPluginEnabled(p.id));
  if (!promos.length) return null;

  if (variant === "inline") {
    return (
      <section className="xl:hidden bg-muted/30 border-t border-border" data-testid="promo-rail-inline">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-10">
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-5">From Learn Me</p>
          <div className="grid sm:grid-cols-2 gap-4"><Cards promos={promos} prefix="inline-" /></div>
        </div>
      </section>
    );
  }
  return (
    <aside className="hidden xl:flex flex-col fixed top-0 right-0 h-screen w-[300px] border-l border-border bg-background/85 backdrop-blur-xl z-30 overflow-y-auto" data-testid="promo-rail">
      <div className="p-5">
        <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-4">From Learn Me</p>
        <div className="flex flex-col gap-4"><Cards promos={promos} /></div>
      </div>
    </aside>
  );
}
